<script setup lang="ts">
import { computed } from "vue";
import { PAGE_SECTION_KINDS } from "@/apps/page/services/page.service";
import { useBlocks } from "@/apps/page/services/blocks/useBlocks";
import {
    AddIcon,
    InvisibleIcon,
    PencilSquareIcon,
    RecycleBinIcon,
    RocketIcon,
    VisibleIcon,
} from "@easy/ui/icons";
import { ButtonTertiaryIconXs } from "@easy/ui/buttons";
import { useActions } from "@/store/vuex-composition-mappers";
import { PageSection } from "@/apps/page/page.interfaces";
import { useSections } from "@/apps/page/services/useSections";
import { SectionToolInfo } from "@/apps/page/section-tool-info.interface";
import { InPageUsedTools } from "@/apps/page/page-project.interface";

const props = defineProps<{ section: PageSection }>();

// @ts-expect-error
const { toggleHideSection, removeSection } = useActions("page", [
    "toggleHideSection",
    "removeSection",
]);

const { getBlockWithTypeMetadata } = useBlocks();
const { sectionTypes } = useSections();

const isBlock = computed(() => props.section.kind === PAGE_SECTION_KINDS.BLOCK);

const sectionDetails = computed(() => {
    if (isBlock.value) {
        const blockWithTypeMetadata = getBlockWithTypeMetadata(
            props.section.typeId || "",
            props.section.type
        );

        if (!blockWithTypeMetadata) {
            return null;
        }

        const { block, blockType } = blockWithTypeMetadata;

        return {
            title: blockType.title,
            subtitle: block.name,
            icon: blockType.icon,
            cssClass: "ct-fg-2",
            wrapperClass: ["ct-bg-3", "text-gray-500 dark:text-neutral-200"],
        };
    }

    // Section is TOOL type, so:
    const { projectData, type, projectId } = props.section;
    const { name, uuid } = projectData || {};
    const toolInfo: SectionToolInfo = {
        isTool: true,
        isPublished: projectData?.access === "published",
        isCreated: Boolean(projectId),
        type: type as InPageUsedTools,
        uuid: projectData?.uuid,
    };
    return {
        title: name || projectId,
        subtitle: `(${type}) /${uuid || ""}`,
        icon: sectionTypes.value.find((s) => s.tool === type)?.icon,
        cssClass: `text-${type}-500`,
        wrapperClass: [`bg-${type}-500`, "bg-opacity-10"],
        toolInfo,
    };
});

const forwardEdit = () => {
    // do nothing
    // @click is asigned on this component and will be handled by the parent component
};
</script>

<template>
    <div
        v-if="sectionDetails"
        class="cursor-pointer ring-1 ring-inset ct-ring-1 hover:ct-ring-brand ct-bg-1--alt rounded-lg px-3 py-3 flex items-center space-x-2 shadow-sm shadow-gray-900/5 group"
        :class="{
            'ct-ring-brand ct-ring-focus': false,
            'opacity-50': section.isHidden,
        }"
    >
        <div class="flex items-center justify-between w-full">
            <div class="flex items-center gap-3 overflow-hidden">
                <div
                    class="w-8 h-8 min-w-8 rounded-lg flex items-center justify-center"
                    :class="sectionDetails.wrapperClass"
                >
                    <component
                        :is="sectionDetails.icon"
                        class="h-4 w-4"
                        :class="sectionDetails.cssClass"
                    />
                </div>
                <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-sm truncate">{{ sectionDetails.title }}</span>
                    <span class="text-xs ct-fg-3 truncate">{{ sectionDetails.subtitle }}</span>
                </div>
            </div>
            <div class="flex items-center">
                <ButtonTertiaryIconXs
                    @click.stop="removeSection(section.id)"
                    class="!hidden group-hover:!block"
                >
                    <RecycleBinIcon />
                </ButtonTertiaryIconXs>
                <ButtonTertiaryIconXs
                    @click.stop="toggleHideSection(section.id)"
                    :class="{ '!hidden group-hover:!block': !section.isHidden }"
                >
                    <VisibleIcon v-if="!section.isHidden" />
                    <InvisibleIcon v-else />
                </ButtonTertiaryIconXs>
                <ButtonTertiaryIconXs @click="forwardEdit">
                    <template v-if="sectionDetails.toolInfo">
                        <AddIcon v-if="!sectionDetails.toolInfo.isCreated" />
                        <RocketIcon v-else-if="!sectionDetails.toolInfo.isPublished" />
                        <PencilSquareIcon v-else />
                    </template>
                    <PencilSquareIcon v-else />
                </ButtonTertiaryIconXs>
            </div>
        </div>
    </div>
</template>
